import { Message } from '../types';

interface ShareImageOptions {
  watermark?: string;
  label?: string;
  botName?: string;
  botAvatar?: string;
  maxMessages?: number;
}

const WIDTH = 1080;
const PADDING = 60;
const BUBBLE_MAX_WIDTH = WIDTH - PADDING * 2;
const LINE_HEIGHT = 46;
const FONT_BODY = '600 32px "Inter", "Segoe UI", "Helvetica Neue", sans-serif';
const FONT_HEADING =
  '700 38px "Inter", "Segoe UI", "Helvetica Neue", sans-serif';

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] {
  const paragraphs = text.split(/\n+/);
  const lines: string[] = [];

  for (const paragraph of paragraphs) {
    const words = paragraph.trim().split(' ');
    let current = '';

    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      const width = ctx.measureText(candidate).width;
      if (width > maxWidth && current) {
        lines.push(current);
        current = word;
      } else {
        current = candidate;
      }
    }

    if (current) {
      lines.push(current);
    }
    lines.push(''); // paragraph spacing
  }

  if (lines.length && lines[lines.length - 1] === '') {
    lines.pop();
  }

  return lines;
}

function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(
    x + width,
    y + height,
    x + width - radius,
    y + height
  );
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

async function loadAvatar(src?: string): Promise<HTMLImageElement | null> {
  if (!src) return null;
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

export async function generateShareImage(
  messages: Message[],
  options: ShareImageOptions = {}
): Promise<Blob> {
  if (messages.length === 0) {
    throw new Error('Няма какво да споделиш.');
  }

  const maxMessages = Math.max(
    2,
    Math.min(options.maxMessages ?? 4, messages.length)
  );
  const slice = messages.slice(-maxMessages);
  const botName = options.botName || 'BOTKOP';
  const watermark = options.watermark || 'botkop.pages.dev';
  const label = options.label || 'Сатира / Пародия';

  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');

  ctx.font = FONT_BODY;
  const bubbleHeights = slice.map((msg) => {
    const lines = wrapText(ctx, msg.content, BUBBLE_MAX_WIDTH - 80);
    return lines.length * LINE_HEIGHT + 60;
  });

  const headerHeight = 140;
  const footerHeight = 90;
  const totalHeight =
    headerHeight + footerHeight + PADDING + bubbleHeights.reduce((a, b) => a + b, 0);

  canvas.height = totalHeight;

  const gradient = ctx.createLinearGradient(0, 0, WIDTH, totalHeight);
  gradient.addColorStop(0, '#0c0f17');
  gradient.addColorStop(1, '#111827');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, WIDTH, totalHeight);

  // header
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.35)';
  ctx.shadowBlur = 24;
  ctx.fillStyle = '#161c2c';
  drawRoundedRect(ctx, PADDING, PADDING, WIDTH - PADDING * 2, headerHeight, 24);
  ctx.fill();
  ctx.restore();

  const avatar = await loadAvatar(options.botAvatar);
  if (avatar) {
    const size = 96;
    const x = PADDING + 28;
    const y = PADDING + (headerHeight - size) / 2;
    ctx.save();
    ctx.beginPath();
    ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(avatar, x, y, size, size);
    ctx.restore();
  }

  ctx.fillStyle = '#f5f7ff';
  ctx.font = FONT_HEADING;
  ctx.fillText(botName, PADDING + 140, PADDING + 82);

  ctx.font = '600 24px "Inter", "Segoe UI", sans-serif';
  ctx.fillStyle = '#cdd6f4';
  ctx.fillText(label, WIDTH - PADDING - ctx.measureText(label).width, PADDING + 42);

  ctx.fillStyle = '#98a2b3';
  ctx.fillText(
    'Последни опорки от чата',
    PADDING + 140,
    PADDING + headerHeight - 28
  );

  // messages
  let offsetY = PADDING + headerHeight + 24;
  slice.forEach((msg, index) => {
    const isBot = msg.role === 'assistant';
    const bubbleWidth = BUBBLE_MAX_WIDTH;
    const bubbleX = isBot ? PADDING : WIDTH - PADDING - bubbleWidth;
    const lines = wrapText(ctx, msg.content, bubbleWidth - 80);
    const bubbleHeight = bubbleHeights[index];

    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.35)';
    ctx.shadowBlur = 18;
    ctx.fillStyle = isBot ? '#b91c1c' : '#111827';
    drawRoundedRect(ctx, bubbleX, offsetY, bubbleWidth, bubbleHeight, 24);
    ctx.fill();
    ctx.restore();

    ctx.fillStyle = isBot ? '#fff7e6' : '#e5e7eb';
    ctx.font = FONT_BODY;
    let textY = offsetY + 40;
    const textX = bubbleX + 40;
    for (const line of lines) {
      ctx.fillText(line, textX, textY);
      textY += LINE_HEIGHT;
    }

    offsetY += bubbleHeight + 26;
  });

  // footer
  ctx.save();
  ctx.fillStyle = '#0f172a';
  drawRoundedRect(
    ctx,
    PADDING,
    totalHeight - footerHeight - PADDING / 3,
    WIDTH - PADDING * 2,
    footerHeight,
    18
  );
  ctx.fill();
  ctx.restore();

  ctx.fillStyle = '#facc15';
  ctx.font = '700 26px "Inter", "Segoe UI", sans-serif';
  ctx.fillText(
    watermark,
    PADDING + 24,
    totalHeight - footerHeight + PADDING / 2
  );

  ctx.fillStyle = '#94a3b8';
  ctx.font = '500 22px "Inter", "Segoe UI", sans-serif';
  ctx.fillText(
    'Сподели гадостта ?',
    WIDTH - PADDING - ctx.measureText('Сподели гадостта ?').width - 24,
    totalHeight - footerHeight + PADDING / 2
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Не успях да направя снимка.'));
        return;
      }
      resolve(blob);
    }, 'image/png');
  });
}

export async function shareBlobOrDownload(
  blob: Blob,
  filename: string,
  preferNative?: boolean,
  text?: string
) {
  const supportsShare =
    typeof navigator !== 'undefined' &&
    'canShare' in navigator &&
    (navigator as Navigator & { canShare?: (data: unknown) => boolean }).canShare?.(
      { files: [new File([blob], filename, { type: blob.type })] }
    );

  if (preferNative && supportsShare && 'share' in navigator) {
    const file = new File([blob], filename, { type: blob.type });
    await (navigator as Navigator & { share?: (data: unknown) => Promise<void> }).share?.(
      {
        files: [file],
        title: 'botkop',
        text,
      }
    );
    return;
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
